'use client';

import { useFrame } from '@react-three/fiber';
import { useSpring } from 'framer-motion';
import clamp from 'lodash/clamp';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { DoubleSide, IUniform, ShaderMaterial, Texture, Uniform } from 'three';

import useAudioRoutingStore from 'state/audioRoutingStore';

import fragmentShader from './shaders/fragment.glsl';
import vertexShader from './shaders/vertex.glsl';

import useKaraokeText from 'hooks/useKaraokeText';

interface AudioInteractiveMaterialProps {
  texture: Texture;
  outline: Texture;
  initialOpacity?: number;
}

export interface AudioInteractiveMaterialRef {
  opacity: IUniform<number>;
}

const AudioInteractiveMaterial = forwardRef<
  AudioInteractiveMaterialRef,
  AudioInteractiveMaterialProps
>(({ texture, outline, initialOpacity = 1.0 }, ref) => {
  const spring = useSpring(0, { stiffness: 100 });

  const materialRef = useRef<ShaderMaterial>(
    new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTexture: new Uniform(texture),
        uOutline: new Uniform(outline),
        uTime: { value: 0.0 },
        uSound: { value: 0.0 },
        uDistortion: { value: 0.05 },
        uLayers: { value: 10.0 },
        uLayerOffset: { value: 0.1 },
        uMotionBlur: { value: 0.0025 },
        uGlowIntensity: { value: 0.0 },
        uOpacity: { value: initialOpacity },
        uWordFactor: { value: 0.0 },
      },
      side: DoubleSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
    })
  );

  useImperativeHandle(ref, () => ({
    opacity: materialRef.current.uniforms.uOpacity,
  }));

  const { wordsSaid } = useKaraokeText();

  useFrame(({ clock }) => {
    const { isPlaying } = useAudioRoutingStore.getState();
    const lastWord = wordsSaid[wordsSaid.length - 1] || '1234'; // "this 1234 is just empty string as default "wave length" if there's no last word
    const last_word_length = lastWord.length;

    const targetWordFactor = clamp(last_word_length / 10, 0, 1);

    if (!materialRef.current) return;

    const smoothingFactor = 0.015; // Lower value results in slower, smoother transitions.
    const currentWordFactor = materialRef.current.uniforms.uWordFactor.value;
    materialRef.current.uniforms.uWordFactor.value =
      currentWordFactor +
      (targetWordFactor - currentWordFactor) * smoothingFactor;

    const pseudoFrequency = isPlaying ? Math.random() : 0;
    materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    materialRef.current.uniforms.uSound.value = spring.get();
    materialRef.current.uniforms.uGlowIntensity.value = pseudoFrequency
      ? pseudoFrequency / 100
      : 0;

    spring.set(pseudoFrequency ? clamp(pseudoFrequency / 100, 1.0, 1.5) : 0);
  });

  return <primitive object={materialRef.current} attach="material" />;
});

AudioInteractiveMaterial.displayName = 'AudioInteractiveMaterial';
export default AudioInteractiveMaterial;
